import { useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { can, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { EVENT_SCOPES } from '../events/labels';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { packlistKey, type PacklistView } from './types';
import { DialogLayer } from '../ui/Dialog';
import { Notice } from '../ui/Notice';

type Found = { type: 'block' | 'template'; id: string; name: string; details: string[] };
type Option = PickerOption & { found: Found };
const FILLED: Record<string, string> = { scopes: 'zakres prac', systemId: 'system' };

/** Whether "Wstaw blok lub szablon…" is offered: the person sees blocks or templates. */
export const mayApply = (me: Me): boolean => can(me, 'templates.blocks', 'view') || can(me, 'templates.templates', 'view');

/**
 * "Wstaw blok lub szablon" (opened from the packlist's "…", owner 2026-10-21 23:39): a quick picker over blocks and
 * templates. "2 mikrofony" inserts the block twice; a template goes in once (its blocks with their own multipliers)
 * and may fill the event's empty planning fields. "Zamknij" (or Escape: first the list, then the text; the event card itself never closes on Escape) puts it away.
 */
export function ApplyMenu({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [done, setDone] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
  }, []);
  if (!me) return null;
  const seeBlocks = can(me, 'templates.blocks', 'view');
  const seeTemplates = can(me, 'templates.templates', 'view');
  if (!seeBlocks && !seeTemplates) return null;

  async function search(q: string, signal: AbortSignal): Promise<Option[]> {
    const param = `?q=${encodeURIComponent(q)}`;
    const [blocks, templates] = await Promise.all([
      seeBlocks ? api<{ blocks: { id: string; name: string; lineCount: number }[] }>(`/api/templates/blocks${param}`, { signal }).then((r) => r.blocks) : Promise.resolve([]),
      seeTemplates ? api<{ templates: { id: string; name: string; scopes?: string[] }[] }>(`/api/templates/templates${param}`, { signal }).then((r) => r.templates) : Promise.resolve([]),
    ]);
    const found: Found[] = [
      ...blocks.slice(0, 10).map((b) => ({ type: 'block' as const, id: b.id, name: b.name, details: ['blok', `pozycji: ${b.lineCount}`] })),
      ...templates.slice(0, 10).map((t) => ({
        type: 'template' as const,
        id: t.id,
        name: t.name,
        details: ['szablon', ...(t.scopes?.length ? [t.scopes.map((s) => EVENT_SCOPES[s as keyof typeof EVENT_SCOPES] ?? s).join(', ')] : [])],
      })),
    ];
    return found.map((f) => ({ key: `${f.type}-${f.id}`, name: f.name, details: f.details, found: f }));
  }

  async function apply(option: Option, quantity: number): Promise<boolean> {
    const { type, id, name } = option.found;
    // one question for the whole block or template: every item other events reserved first (owner 2026-10-24)
    const view = await withConflictConfirm((confirmConflicts) =>
      api<PacklistView & { filled?: string[] }>(`/api/packlists/${eventId}/${type === 'block' ? 'apply-block' : 'apply-template'}`, {
        method: 'POST',
        body: { ...(type === 'block' ? { blockId: id, multiplier: quantity } : { templateId: id }), ...(confirmConflicts ? { confirmConflicts } : {}) },
      }),
    );
    if (!view) return false;
    const { filled = [], ...packlist } = view;
    queryClient.setQueryData(packlistKey(eventId), packlist);
    if (filled.length > 0) await queryClient.invalidateQueries({ queryKey: ['events'] });
    setDone(
      `${type === 'block' ? `Wstawiono blok „${name}”${quantity > 1 ? ` ×${quantity}` : ''}` : `Wstawiono szablon „${name}”`}.` +
        (filled.length > 0 ? ` Uzupełniono w wydarzeniu: ${filled.map((f) => FILLED[f] ?? f).join(', ')}.` : ''),
    );  return true;
  }

  return (
    <div className="apply-menu panel" data-feature-id="packlists.apply">
      <div className="apply-menu-head">
        <QuickPicker<Option>
          label="Wstaw blok lub szablon"
          placeholder="np. mikrofony, 2x okablowanie, koncert"
          search={search}
          onPick={apply}
          inputRef={input}
          featureId="packlists.apply"
        />
        <DialogLayer onClose={onClose} />
        <button className="btn ghost small" type="button" onClick={onClose}>
          Zamknij
        </button>
      </div>
      {done && (
        <Notice onClose={() => setDone('')}>
          {done}
        </Notice>
      )}
    </div>
  );
}
